import { useState } from 'react'
import type { KeyboardEvent } from 'react'

import { ChevronDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import './FilterSelect.css'

/**
 * Highlight in the selected blue the sidebar and project switcher use — the
 * preset default is a gray `bg-muted`. `min-h-11` is the 44px touch-target
 * minimum (React_Web_Standards §11.1).
 */
const ITEM_CLASS =
  'min-h-11 data-selected:bg-(--surface-selected) data-selected:text-(--blattner-dark-blue)'

type Props = {
  /** The applied value. `undefined` means the filter is off. */
  value?: string
  /** Called with `undefined` when the clear row is chosen. */
  onChange: (value: string | undefined) => void
  /** The values on offer, already sorted by the caller. */
  options: readonly string[]
  /** The control's subject — "Carrier". Read to a screen reader, not shown. */
  label: string
  /**
   * Trigger text while nothing is applied, and the clear row's label — "All
   * carriers". A value rather than a label: the trigger's job is to report
   * what the board is currently showing.
   */
  placeholder: string
  /** Matches the icon the tiles use for the same field. */
  icon: LucideIcon
  searchPlaceholder?: string
  emptyText?: string
}

/**
 * A searchable single-select filter: trigger showing its own value, panel
 * with a search box, a clear row, and the options.
 *
 * Controlled on purpose. The caller owns the value — on the delivery board
 * that is a URL search param — which is what makes "clear every filter" one
 * write to one owner rather than a reach into each control.
 *
 * Popover + Command per the shadcn combobox pattern, the same pairing
 * ProjectSwitcher uses: cmdk brings the filtering, keyboard navigation and
 * listbox semantics, Radix brings the portal, dismissal and placement.
 */
function FilterSelect({
  value,
  onChange,
  options,
  label,
  placeholder,
  icon: Icon,
  searchPlaceholder,
  emptyText,
}: Props) {
  const [open, setOpen] = useState(false)

  const choose = (next: string | undefined) => {
    onChange(next)
    setOpen(false)
  }

  // A collapsed combobox is expected to open on ArrowDown. Radix's popover
  // trigger is a plain button, so it handles Enter and Space but not this.
  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault()
      setOpen(true)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={
          value
            ? 'filter-select__trigger filter-select__trigger--applied'
            : 'filter-select__trigger'
        }
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        /* Subject and value both, because a combobox takes no name from its
           own content — "Carrier: Dakota Freight Lines". Sighted users read
           the subject off the icon, which the tiles share; a screen reader
           has no icon to read. */
        aria-label={`${label}: ${value ?? placeholder}`}
        onKeyDown={handleTriggerKeyDown}
      >
        <Icon aria-hidden="true" />
        <span className="filter-select__value">{value ?? placeholder}</span>
        <ChevronDown className="filter-select__chevron" aria-hidden="true" />
      </PopoverTrigger>

      <PopoverContent
        className="w-(--radix-popover-trigger-width) min-w-56 p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}…`}
          />
          <CommandList>
            <CommandEmpty>{emptyText ?? 'No match.'}</CommandEmpty>
            <CommandGroup>
              {/* The un-filter lives at the top of the list rather than as a
                  separate ✕ in the strip: changing the value and clearing it
                  are the same errand, so they belong in the same place.
                  forceMount keeps it reachable while a search is narrowing
                  everything else away. */}
              <CommandItem
                forceMount
                className={`${ITEM_CLASS} text-(--fg3)`}
                value={placeholder}
                data-checked={value === undefined}
                onSelect={() => choose(undefined)}
              >
                {placeholder}
              </CommandItem>

              {options.map((option) => (
                <CommandItem
                  key={option}
                  className={ITEM_CLASS}
                  value={option}
                  data-checked={option === value}
                  onSelect={() => choose(option)}
                >
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default FilterSelect
